import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Chip,
    Grid2,
    IconButton,
    Divider,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import PolicyIcon from '@mui/icons-material/Policy';
import RuleIcon from '@mui/icons-material/Rule';
import { IPricePoliciesEntity } from '../../../../Services/api/price-policies/price-policies.interface';

interface ISelectedPoliciesDisplayProps {
    selectedPolicies: IPricePoliciesEntity[];
    onRemovePolicy?: (policyId: string) => void;
    showRemoveButton?: boolean;
    variant?: 'compact' | 'detailed';
    title?: string;
}

const SelectedPoliciesDisplay: React.FC<ISelectedPoliciesDisplayProps> = ({
    selectedPolicies,
    onRemovePolicy,
    showRemoveButton = true,
    variant = 'detailed',
    title = 'Políticas de Preço Selecionadas'
}) => {
    const theme = useTheme();

    if (selectedPolicies.length === 0) {
        return (
            <Box
                sx={{
                    width: '100%',
                    textAlign: 'center',
                    py: 4,
                    px: 2,
                    border: `1px dashed ${theme.palette.divider}`,
                    borderRadius: 2,
                    
                }}
            >
                <PolicyIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                    Nenhuma política selecionada
                </Typography>
                {/* <Typography variant="body2" color="textSecondary">
                    Clique em "Adicionar Política" para selecionar políticas de preço
                </Typography> */}
            </Box>
        );
    }

    if (variant === 'compact') {
        return (
            <Box sx={{ width: '100%' }}>
                <Typography variant="subtitle1" gutterBottom>
                    {title} ({selectedPolicies.length})
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1} sx={{ width: '100%' }}>
                    {selectedPolicies.map((policy) => (
                        <Chip
                            key={policy.id}
                            label={`${policy.name} (${policy.rules?.length || 0} regras)`}
                            color={policy.isActive ? 'primary' : 'default'}
                            variant="outlined"
                            onDelete={showRemoveButton && onRemovePolicy ? () => onRemovePolicy(policy.id) : undefined}
                            deleteIcon={<DeleteIcon />}
                        />
                    ))}
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%', minWidth: 0 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Typography variant="h6" gutterBottom>
                    {title}
                </Typography>
                <Chip
                    label={`${selectedPolicies.length} política(s)`}
                    size="small"
                    color="primary"
                    variant="outlined"
                />
            </Box>

            <Grid2 container spacing={2} sx={{ width: '100%' }}>
                {selectedPolicies.map((policy, index) => (
                    <Grid2 size={12} key={policy.id}>
                        <Card 
                            variant="outlined"
                            sx={{
                                width: '100%',
                                backgroundColor: policy.isActive 
                                    ? theme.palette.background.paper
                                    : theme.palette.grey[50],
                                border: policy.isActive 
                                    ? `1px solid ${theme.palette.primary.light}`
                                    : `1px solid ${theme.palette.grey[300]}`
                            }}
                        >
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                    <Box flex={1}>
                                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                                            <PolicyIcon color={policy.isActive ? 'primary' : 'disabled'} />
                                            <Typography variant="h6" component="h3">
                                                {policy.name}
                                            </Typography>
                                            <Chip
                                                label={policy.isActive ? 'Ativa' : 'Inativa'}
                                                size="small"
                                                color={policy.isActive ? 'success' : 'default'}
                                                variant="outlined"
                                            />
                                        </Box>
                                        {/* {policy.description && (
                                            <Typography variant="body2" color="textSecondary" mb={2}>
                                                {policy.description}
                                            </Typography>
                                        )} */}
                                    </Box>
                                    {showRemoveButton && onRemovePolicy && (
                                        <IconButton
                                            onClick={() => onRemovePolicy(policy.id)}
                                            size="small"
                                            color="error"
                                            sx={{ ml: 1 }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </Box>

                                {/* Informações da Política */}
                                {/* <Box mb={2}>
                                    <Typography variant="body2" color="textSecondary">
                                        <strong>ID:</strong> {policy.id}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        <strong>Slug:</strong> {policy.slug}
                                    </Typography>
                                </Box> */}

                                {/* Regras da Política */}
                                <Accordion defaultExpanded={index === 0}>
                                    <AccordionSummary
                                        expandIcon={<ExpandMoreIcon />}
                                        sx={{
                                            backgroundColor: theme.palette.grey[50],
                                            '&:hover': {
                                                backgroundColor: theme.palette.grey[100]
                                            }
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <RuleIcon color="primary" />
                                            <Typography variant="subtitle1">
                                                Regras de Desconto ({policy.rules?.length || 0})
                                            </Typography>
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {!policy.rules || policy.rules.length === 0 ? (
                                            <Box textAlign="center" py={2}>
                                                <Typography color="textSecondary">
                                                    Esta política não possui regras cadastradas
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <TableContainer component={Paper} variant="outlined" sx={{ width: '100%' }}>
                                                <Table size="small" sx={{ width: '100%' }}>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell><strong>Descrição</strong></TableCell>
                                                            <TableCell align="center"><strong>Quantidade Mínima</strong></TableCell>
                                                            <TableCell align="center"><strong>Desconto (%)</strong></TableCell>
                                                            <TableCell align="center"><strong>Status</strong></TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {policy.rules.map((rule) => (
                                                            <TableRow key={rule.id} hover>
                                                                <TableCell>
                                                                    <Typography variant="body2" sx={{ color: '#666' }}>
                                                                        Se quantidade ≥ {rule.minimalQuantity}, aplicar {rule.discountPercentage}% de desconto
                                                                    </Typography>
                                                                    <Typography variant="caption" sx={{ color: '#999', display: 'block', marginTop: '5px' }}>
                                                                        Preço final: {((100 - (rule.discountPercentage || 0)) / 100).toFixed(2)}x o preço original
                                                                    </Typography>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Chip
                                                                        label={rule.minimalQuantity}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        color="info"
                                                                    />
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Box
                                                                        component="span"
                                                                        sx={{
                                                                            fontWeight: 'bold',
                                                                            color: '#FFF',
                                                                            backgroundColor: theme.palette.success.light,
                                                                            px: 1,
                                                                            py: 0.5,
                                                                            borderRadius: 1,
                                                                            fontSize: '0.875rem'
                                                                        }}
                                                                    >
                                                                        {rule.discountPercentage}%
                                                                    </Box>
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    <Chip
                                                                        label={rule.isActive ? 'Ativa' : 'Inativa'}
                                                                        size="small"
                                                                        color={rule.isActive ? 'success' : 'default'}
                                                                        variant={rule.isActive ? 'filled' : 'outlined'}
                                                                    />
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        )}
                                    </AccordionDetails>
                                </Accordion>
                            </CardContent>
                        </Card>
                    </Grid2>
                ))}
            </Grid2>
        </Box>
    );
};

export default SelectedPoliciesDisplay;
